import { Component, ChangeDetectionStrategy, Injector, inject } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { BaseResizableComponent } from '@src/app/modules/base';
import { UserUI } from '@src/models';
import { UserService } from '@src/core/services';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import { NEW_USERS_HINT } from '@src/app/modules/hint/constants';
import { PermissionService } from '@src/app/modules/user-info/services';

@Component({
  selector: 'app-new-users',
  templateUrl: './new-users.component.html',
  styleUrls: ['./new-users.component.scss', '../styles/administration-content.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewUsersComponent extends BaseResizableComponent {
  private readonly userService = inject(UserService);
  private readonly translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);

  unregistredUsers$: BehaviorSubject<UserUI[] | null> = this.userService.unregistredUsers$;
  selectedUserId?: string;

  readonly newUsersHint = this.translateService.instant(NEW_USERS_HINT);

  private readonly confirmDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.userDeleteHeader'),
      size: 's',
      closeable: false,
    },
  );

  get showFullPage(): boolean {
    return !this.selectedUserId;
  }

  get allowDeleting(): boolean {
    return this.permissionService.allowDeleting$.value && this.selectedUserId !== this.userService.authUser$.value?.id;
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.getData();
  }

  onClickReturnButton(): void {
    this.selectedUserId = undefined;
    this.cdr.markForCheck();
  }

  changeSelectedUserId(selectedId?: string) {
    this.selectedUserId = selectedId;
  }

  onSaveEditingUser(): void {
    this.getData();
  }

  onDeleteUser(user?: UserUI): void {
    const id = user?.id;
    if (!id) return;

    this.confirmDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        if (res === true) {
          lastValueFrom(this.userService.deleteUser(id)).then(() => {
            this.selectedUserId = undefined;
            this.getData();
          });
        }
      },
    });
  }

  private getData(): void {
    this.userService.resetUnregistredUsers();
    this.userService.getUnregistredUsers();
    this.cdr.markForCheck();
  }
}
